import { AudioClip, TranscribeResponse, VoiceCriteria } from "./types";
import { encodeWav } from "./wav";

/**
 * Server-side speech-to-text. The endpoint runs the transcription and the
 * interpretation in one pass, so its response is an InterpretResponse plus the
 * transcript — there is no second /Voice/Interpret call on the happy path.
 */
const TRANSCRIBE_PATH = "/Voice/Transcribe";

/** Server hard limit. Enforced client-side too so an oversized clip fails before the upload. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * Formats the endpoint accepts: .webm/.wav/.mp3/.m4a/.ogg. MediaRecorder only
 * ever produces the first, the last, or MP4 audio (Safari) — which is the m4a
 * container, hence the .m4a extension for it. Anything unrecognised falls back
 * to WAV, which is always derivable from pcm16k.
 */
const EXTENSION_BY_MIME: { match: RegExp; extension: string }[] = [
  { match: /^audio\/webm/, extension: "webm" },
  { match: /^audio\/ogg/, extension: "ogg" },
  { match: /^audio\/(mp4|m4a|x-m4a)/, extension: "m4a" },
  { match: /^audio\/mpeg/, extension: "mp3" },
  { match: /^audio\/wav|^audio\/x-wav|^audio\/wave/, extension: "wav" },
];

function extensionFor(mimeType: string): string | null {
  const hit = EXTENSION_BY_MIME.find((entry) => entry.match.test(mimeType));
  return hit ? hit.extension : null;
}

export class UnsupportedAudioError extends Error {}
export class AudioTooLargeError extends Error {}

/**
 * Picks what actually goes on the wire. Prefers the compressed MediaRecorder
 * blob (an Opus clip is roughly a tenth the size of the equivalent WAV), and
 * falls back to a WAV built from pcm16k whenever that blob is missing or in a
 * container the endpoint does not list.
 */
export function buildUploadFile(clip: AudioClip): { blob: Blob; filename: string } {
  const encoded = clip.encoded;
  if (encoded && encoded.blob.size > 0) {
    // MediaRecorder reports the full codec string ("audio/webm;codecs=opus");
    // match on the container only.
    const extension = extensionFor(encoded.mimeType.split(";")[0].trim());
    if (extension) {
      return { blob: encoded.blob, filename: `speech.${extension}` };
    }
  }

  const wavBytes = clip.wavBytes ?? encodeWav(clip.pcm16k);
  // Copy into a fresh ArrayBuffer: a Uint8Array view over a larger pooled
  // buffer would otherwise upload the whole backing store.
  const body = new Uint8Array(wavBytes.length);
  body.set(wavBytes);
  return { blob: new Blob([body], { type: "audio/wav" }), filename: "speech.wav" };
}

/**
 * Injected rather than importing axios here, for the same reason
 * interpretClient.ts does it: web and native reach the network differently and
 * this file has to stay platform-agnostic.
 */
export type TranscribeHttpDeps = {
  postMultipart: (path: string, form: FormData, signal?: AbortSignal) => Promise<TranscribeResponse>;
  generateId: () => string;
};

export type TranscribeArgs = {
  clip: AudioClip;
  languageHint: string;
  existingCriteria: Partial<VoiceCriteria>;
  signal?: AbortSignal;
};

export async function transcribe(
  deps: TranscribeHttpDeps,
  args: TranscribeArgs,
): Promise<TranscribeResponse> {
  const { blob, filename } = buildUploadFile(args.clip);

  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new AudioTooLargeError(`clip is ${blob.size} bytes, limit is ${MAX_UPLOAD_BYTES}`);
  }
  if (blob.size === 0) {
    throw new UnsupportedAudioError("no audio captured");
  }

  // PascalCase field names are the backend's multipart contract — do not
  // rename without checking with the backend team.
  const form = new FormData();
  form.append("Audio", blob, filename);
  form.append("ReferenceTimestamp", new Date().toISOString());
  form.append("ReferenceTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
  if (args.languageHint) form.append("LanguageHint", args.languageHint);
  form.append("ExistingCriteria", JSON.stringify(args.existingCriteria));
  form.append("CorrelationId", deps.generateId());

  return deps.postMultipart(TRANSCRIBE_PATH, form, args.signal);
}
